/**
 * States of Mind AI Navigator — onboarding logic (MVP).
 * Classifies users into audiences and returns predefined 3-step paths.
 */

export const AUDIENCES = {
    CURIOUS: 'curious_newbie',
    MENTAL_HEALTH: 'mental_health_support_seeker',
    LONGEVITY: 'longevity_biohacking_enthusiast',
    DEEP_DIVER: 'experienced_deep_diver',
};

export const INTRO = {
    title: 'AI Navigator',
    body:
        "Hi, I can help you find the best place to start inside States of Mind.\n\n" +
        "Answer a few quick questions and I'll recommend a simple starting path based on what you're looking for.",
    cta: 'Start',
};

export const SAFETY_DISCLAIMER =
    'This is educational content — not medical advice. Consider speaking with a qualified professional for personal decisions.';

export const CRISIS_RESPONSE =
    "I'm really glad you reached out. What you're describing sounds urgent, and this navigator can't provide crisis support or medical advice.\n\n" +
    'Please contact local emergency services or a crisis line right now. In the US you can call or text 988. In the UK you can call 116 123. You can also visit findahelpline.com.\n\n' +
    'If you are not in immediate danger, consider speaking with a qualified mental health professional.';

const CRISIS_PATTERNS = [
    /\b(suicid|kill myself|end my life|want to die|self[- ]?harm|hurt myself)\b/i,
    /\b(having thoughts of (?:suicide|dying|killing))\b/i,
    /\b(psychosis|psychotic|hallucinat|hearing voices)\b/i,
    /\b(mania|manic episode)\b/i,
    /\b(overdose|od'?d)\b/i,
    /\b(pregnant|pregnancy).{0,40}(psychedelic|ketamine|mdma|lSD|mushroom)/i,
    /\b(emergency|immediate danger|right now.{0,30}(hurt|kill))\b/i,
];

export function checkCrisis(text) {
    if (!text || typeof text !== 'string') return false;
    return CRISIS_PATTERNS.some((re) => re.test(text));
}

export function initScores() {
    return {
        [AUDIENCES.CURIOUS]: 0,
        [AUDIENCES.MENTAL_HEALTH]: 0,
        [AUDIENCES.LONGEVITY]: 0,
        [AUDIENCES.DEEP_DIVER]: 0,
    };
}

export function initNavigatorState() {
    return {
        complete: false,
        step: 'intro',
        scores: initScores(),
        answers: {},
        audience: null,
        q1Key: null,
        q2Key: null,
        q2Variant: null,
        mentalHealthSelected: false,
        q4DeepBias: false,
        primaryPathUrl: null,
    };
}

/** @type {Record<string, { path: string, title: string, description: string, type: string, duration?: string }>} */
export const RESOURCES = {
    psychedelic_therapy_hub: {
        path: '/toolkit/therapy-guides/',
        title: 'Therapy Guides hub',
        description: 'A structured overview of psychedelic therapy — what it is, how it works, and what to explore first.',
        type: 'hub',
    },
    substance_guides: {
        path: '/toolkit/substance-guides/',
        title: 'Substance Guides',
        description: 'Evidence-based guides to help you understand different substances and approaches.',
        type: 'hub',
    },
    human_journeys_podcast: {
        path: '/podcast/',
        title: 'Human Journeys podcast',
        description: 'Real stories and conversations that bring these topics to life.',
        type: 'podcast',
    },
    psychedelic_therapy_starter: {
        path: '/tests/psychedelic-therapy-starter/',
        title: 'Psychedelic Therapy Starter',
        description: 'A gentle starting point to see whether psychedelic therapy might be worth exploring for you.',
        type: 'screening',
        duration: '5 min',
    },
    psychedelic_therapy_test: {
        path: '/toolkit/screening/psychedelic-therapy-starter/',
        title: 'Psychedelic Therapy Test',
        description: 'A brief reflection to help you think through relevance and next questions — not a diagnosis.',
        type: 'screening',
        duration: '5 min',
    },
    research_tool: {
        path: '/research/',
        title: 'Psychedelic Research Tool',
        description: 'Explore clinical evidence and research papers in one place.',
        type: 'research',
    },
    psilocybin: {
        path: '/tag/psilocybin/',
        title: 'Psilocybin content',
        description: 'Articles and expert perspectives focused on psilocybin.',
        type: 'hub',
    },
    ketamine: {
        path: '/tag/ketamine/',
        title: 'Ketamine content',
        description: 'Learn how ketamine-assisted therapy is discussed in clinical contexts.',
        type: 'hub',
    },
    preparation_assistant: {
        path: '/toolkit/tools/psychedelic-preparation-assistant/',
        title: 'Psychedelic Preparation Assistant',
        description: 'A practical tool to help you think through preparation — educational, not prescriptive.',
        type: 'tool',
    },
    integration: {
        path: '/tag/integration/',
        title: 'Integration content',
        description: 'Resources on integrating experiences into everyday life with care.',
        type: 'hub',
    },
    community: {
        path: '/community/',
        title: 'Community',
        description: 'Connect with others exploring similar questions in a moderated space.',
        type: 'community',
    },
    all_screenings: {
        path: '/toolkit/screening/',
        title: 'All Screenings',
        description: 'Browse self-reflection tools across mental health and wellbeing topics.',
        type: 'screening',
    },
    mental_health_support: {
        path: '/category/stories/',
        title: 'Stories hub',
        description: 'Curated personal stories and journeys when you want lived experience.',
        type: 'hub',
    },
    depression_screening: {
        path: '/toolkit/screening/depression/',
        title: 'Depression screening',
        description: 'A brief check-in on low mood patterns — helpful for reflection, not diagnosis.',
        type: 'screening',
        duration: '3 min',
    },
    anxiety_screening: {
        path: '/toolkit/screening/anxiety/',
        title: 'Anxiety screening',
        description: 'Explore anxiety and stress patterns and what might help you learn more.',
        type: 'screening',
        duration: '3 min',
    },
    ptsd_screening: {
        path: '/toolkit/screening/ptsd/',
        title: 'PTSD screening',
        description: 'Reflect on trauma-related symptoms and possible next steps to explore.',
        type: 'screening',
        duration: '3 min',
    },
    burnout_screening: {
        path: '/toolkit/screening/burnout/',
        title: 'Burnout screening',
        description: 'Check in on exhaustion and burnout — a starting point for understanding.',
        type: 'screening',
        duration: '3 min',
    },
    adhd_screening: {
        path: '/toolkit/screening/adhd/',
        title: 'ADHD screening',
        description: 'Reflect on focus and attention patterns at your own pace.',
        type: 'screening',
        duration: '3 min',
    },
    fatigue: {
        path: '/tag/fatigue/',
        title: 'Fatigue content',
        description: 'Articles on energy, recovery, and when to seek professional support.',
        type: 'hub',
    },
    insomnia: {
        path: '/tag/insomnia/',
        title: 'Insomnia content',
        description: 'Expert content on sleep challenges and recovery approaches.',
        type: 'hub',
    },
    wellness: {
        path: '/tag/wellness/',
        title: 'Wellness content',
        description: 'Practical content on balance, stress, and everyday wellbeing.',
        type: 'hub',
    },
    longevity_hub: {
        path: '/category/stories/',
        title: 'Stories hub',
        description: 'Long-term wellbeing, performance, and body-mind health in one place.',
        type: 'hub',
    },
    libido_screening: {
        path: '/toolkit/screening/libido/',
        title: 'Libido screening',
        description: 'Reflect on vitality and body-mind health as a starting point.',
        type: 'screening',
        duration: '3 min',
    },
};

const Q1 = {
    id: 'q1',
    question: 'What brings you to States of Mind today?',
    options: [
        { id: 'curious', label: "I'm curious about psychedelics and want to understand the basics", audiences: { [AUDIENCES.CURIOUS]: 3 }, q2Variant: 'a' },
        { id: 'mental_health', label: "I'm looking for support with my mental health", audiences: { [AUDIENCES.MENTAL_HEALTH]: 3 }, q2Variant: 'b', mentalHealth: true },
        { id: 'longevity', label: "I'm interested in performance, longevity, sleep, focus, or wellbeing", audiences: { [AUDIENCES.LONGEVITY]: 3 }, q2Variant: 'c' },
        { id: 'deep', label: 'I already know the basics and want deeper content', audiences: { [AUDIENCES.DEEP_DIVER]: 3 }, q2Variant: 'd' },
        { id: 'unsure', label: "I'm not sure yet", q2Variant: 'e' },
    ],
};

const Q2_VARIANTS = {
    a: {
        id: 'q2a',
        question: 'What are you hoping to figure out about psychedelics?',
        options: [
            { id: 'basics', label: 'What psychedelics are and how they work', packKey: 'curious_basics' },
            { id: 'relevant', label: 'Whether psychedelic therapy could be relevant for me', packKey: 'curious_relevant', audiences: { [AUDIENCES.CURIOUS]: 2 } },
            { id: 'science', label: 'What the science actually says', packKey: 'curious_science', audiences: { [AUDIENCES.DEEP_DIVER]: 1 } },
            { id: 'prepare', label: 'How to prepare for a possible experience safely', packKey: 'curious_prepare' },
            { id: 'stories', label: "What real people's experiences are like", packKey: 'curious_stories' },
            { id: 'unsure', label: "I'm not sure — I just want a safe place to start", packKey: 'curious_unsure', audiences: { [AUDIENCES.CURIOUS]: 2 } },
        ],
    },
    b: {
        id: 'q2b',
        question: 'What are you hoping to better understand or improve?',
        options: [
            { id: 'depression', label: 'Low mood, depression, or emotional numbness', packKey: 'mh_depression', mentalHealth: true, audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 } },
            { id: 'anxiety', label: 'Anxiety, stress, or feeling constantly on edge', packKey: 'mh_anxiety', mentalHealth: true, audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 } },
            { id: 'trauma', label: 'Trauma, PTSD, or difficult past experiences', packKey: 'mh_trauma', mentalHealth: true, audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 } },
            { id: 'burnout', label: 'Burnout, exhaustion, or feeling stuck', packKey: 'mh_burnout', mentalHealth: true, audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 } },
            { id: 'adhd', label: 'Focus, ADHD, or racing thoughts', packKey: 'mh_adhd', mentalHealth: true, audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 } },
            { id: 'unsure', label: "I'm not sure — I just know something feels off", packKey: 'mh_unsure', mentalHealth: true, audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 } },
        ],
    },
    c: {
        id: 'q2c',
        question: 'What are you trying to optimize first?',
        options: [
            { id: 'energy', label: 'Energy and fatigue', packKey: 'long_energy', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
            { id: 'sleep', label: 'Sleep and recovery', packKey: 'long_sleep', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
            { id: 'focus', label: 'Focus, cognition, or brain fog', packKey: 'long_focus', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
            { id: 'stress', label: 'Stress resilience and emotional balance', packKey: 'long_stress', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
            { id: 'longevity', label: 'Long-term wellbeing and longevity', packKey: 'long_longevity', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
            { id: 'libido', label: 'Libido, vitality, or body-mind health', packKey: 'long_libido', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
        ],
    },
    d: {
        id: 'q2d',
        question: 'What kind of depth are you looking for?',
        options: [
            { id: 'evidence', label: 'Clinical evidence and research', packKey: 'deep_evidence', audiences: { [AUDIENCES.DEEP_DIVER]: 2 } },
            { id: 'integration', label: 'Integration, preparation, and real-world practice', packKey: 'deep_integration', audiences: { [AUDIENCES.DEEP_DIVER]: 2 } },
            { id: 'substances', label: 'Specific substances like psilocybin, ketamine, cannabis, or microdosing', packKey: 'deep_substances', audiences: { [AUDIENCES.DEEP_DIVER]: 2 } },
            { id: 'mechanisms', label: 'Mental health conditions and treatment mechanisms', packKey: 'deep_mechanisms', audiences: { [AUDIENCES.DEEP_DIVER]: 1, [AUDIENCES.MENTAL_HEALTH]: 1 } },
            { id: 'experts', label: 'Expert perspectives, stories, and emerging debates', packKey: 'deep_experts', audiences: { [AUDIENCES.DEEP_DIVER]: 2 } },
        ],
    },
    e: {
        id: 'q2e',
        question: 'Which of these sounds closest to what you need?',
        options: [
            { id: 'gentle', label: 'I want a gentle introduction to psychedelics', audiences: { [AUDIENCES.CURIOUS]: 2 }, q2VariantOverride: 'a', packKey: 'curious_unsure' },
            { id: 'emotional', label: "I'm struggling emotionally and looking for support", audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 }, q2VariantOverride: 'b', packKey: 'mh_unsure', mentalHealth: true },
            { id: 'optimize', label: 'I want to improve energy, sleep, focus, or wellbeing', audiences: { [AUDIENCES.LONGEVITY]: 2 }, q2VariantOverride: 'c', packKey: 'long_longevity' },
            { id: 'evidence', label: 'I want serious, evidence-based content', audiences: { [AUDIENCES.DEEP_DIVER]: 2 }, q2VariantOverride: 'd', packKey: 'deep_evidence' },
            { id: 'browse', label: 'I just want to browse and see what resonates', audiences: { [AUDIENCES.CURIOUS]: 2 }, packKey: 'curious_unsure' },
        ],
    },
};

const Q3 = {
    id: 'q3',
    question: 'Which topic feels most relevant to you right now?',
    options: [
        { id: 'psychedelics', label: 'Psychedelics and psychedelic therapy', audiences: { [AUDIENCES.CURIOUS]: 1, [AUDIENCES.DEEP_DIVER]: 1 } },
        { id: 'mental_health', label: 'Depression, anxiety, PTSD, ADHD, burnout, or other mental health concerns', audiences: { [AUDIENCES.MENTAL_HEALTH]: 2 }, mentalHealth: true },
        { id: 'sleep_energy', label: 'Sleep, energy, focus, stress, or brain fog', audiences: { [AUDIENCES.LONGEVITY]: 2 } },
        { id: 'advanced', label: 'Microdosing, integration, substances, or advanced psychedelic topics', audiences: { [AUDIENCES.DEEP_DIVER]: 2 } },
        { id: 'unknown', label: "I don't know yet" },
    ],
};

const Q4 = {
    id: 'q4',
    question: 'How familiar are you with this topic?',
    options: [
        { id: 'new', label: "I'm completely new", audiences: { [AUDIENCES.CURIOUS]: 2 } },
        { id: 'some', label: "I've read a little, but still need structure", audiences: { [AUDIENCES.CURIOUS]: 1 } },
        { id: 'tried', label: "I've tried some tools, screenings, or practices before" },
        { id: 'deep', label: 'I already understand the basics and want to go deeper', audiences: { [AUDIENCES.DEEP_DIVER]: 2 }, deepBias: true },
    ],
};

export function getQuestion(step, navState) {
    if (step === 'q1') return Q1;
    if (step === 'q2') {
        const variant = navState.q2Variant || 'a';
        return Q2_VARIANTS[variant] || Q2_VARIANTS.a;
    }
    if (step === 'q3') return Q3;
    if (step === 'q4') return Q4;
    return null;
}

export function applyAnswer(navState, questionId, option) {
    if (!navState.scores) navState.scores = initScores();

    if (option.audiences) {
        Object.entries(option.audiences).forEach(([audience, points]) => {
            navState.scores[audience] = (navState.scores[audience] || 0) + points;
        });
    }

    if (option.mentalHealth) navState.mentalHealthSelected = true;
    if (option.deepBias) navState.q4DeepBias = true;
    if (option.packKey) navState.q2Key = option.packKey;

    navState.answers[questionId] = option.id;

    if (questionId === 'q1') {
        navState.q1Key = option.id;
        navState.q2Variant = option.q2Variant || 'a';
    }

    if (questionId === 'q2e' && option.q2VariantOverride) {
        navState.q2Variant = option.q2VariantOverride;
    }
}

export function classifyAudience(navState) {
    const scores = navState.scores || initScores();
    const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    let audience = ranked[0][0];
    const topScore = ranked[0][1];
    const tied = ranked.filter(([, s]) => s === topScore).map(([a]) => a);

    if (navState.mentalHealthSelected && tied.includes(AUDIENCES.MENTAL_HEALTH)) {
        audience = AUDIENCES.MENTAL_HEALTH;
    } else if (navState.q4DeepBias && tied.includes(AUDIENCES.DEEP_DIVER)) {
        audience = AUDIENCES.DEEP_DIVER;
    } else if (tied.length > 1) {
        if (tied.includes(AUDIENCES.MENTAL_HEALTH) && navState.mentalHealthSelected) {
            audience = AUDIENCES.MENTAL_HEALTH;
        } else if (tied.includes(AUDIENCES.DEEP_DIVER) && navState.q4DeepBias) {
            audience = AUDIENCES.DEEP_DIVER;
        } else {
            audience = AUDIENCES.CURIOUS;
        }
    }

    if (topScore === 0) audience = AUDIENCES.CURIOUS;

    navState.audience = audience;
    return audience;
}

/** @returns {{ primary: object, supporting1: object, supporting2: object }} */
function pack(primaryKey, s1Key, s2Key) {
    return {
        primary: RESOURCES[primaryKey],
        supporting1: RESOURCES[s1Key],
        supporting2: RESOURCES[s2Key],
    };
}

const PACKS = {
    curious_default: () => pack('psychedelic_therapy_hub', 'substance_guides', 'human_journeys_podcast'),
    curious_basics: () => pack('psychedelic_therapy_hub', 'substance_guides', 'human_journeys_podcast'),
    curious_relevant: () => pack('psychedelic_therapy_starter', 'psychedelic_therapy_test', 'psychedelic_therapy_hub'),
    curious_science: () => pack('psychedelic_therapy_hub', 'research_tool', 'psilocybin'),
    curious_prepare: () => pack('preparation_assistant', 'integration', 'substance_guides'),
    curious_stories: () => pack('human_journeys_podcast', 'community', 'psychedelic_therapy_hub'),
    curious_unsure: () => pack('psychedelic_therapy_hub', 'substance_guides', 'human_journeys_podcast'),

    mh_default: () => pack('all_screenings', 'mental_health_support', 'psychedelic_therapy_test'),
    mh_depression: () => pack('depression_screening', 'mental_health_support', 'psychedelic_therapy_test'),
    mh_anxiety: () => pack('anxiety_screening', 'mental_health_support', 'psychedelic_therapy_test'),
    mh_trauma: () => pack('ptsd_screening', 'mental_health_support', 'psychedelic_therapy_test'),
    mh_burnout: () => pack('burnout_screening', 'mental_health_support', 'psychedelic_therapy_test'),
    mh_adhd: () => pack('adhd_screening', 'mental_health_support', 'psychedelic_therapy_test'),
    mh_unsure: () => pack('all_screenings', 'mental_health_support', 'psychedelic_therapy_test'),

    long_default: () => pack('longevity_hub', 'wellness', 'research_tool'),
    long_energy: () => pack('fatigue', 'longevity_hub', 'wellness'),
    long_sleep: () => pack('insomnia', 'longevity_hub', 'wellness'),
    long_focus: () => pack('adhd_screening', 'longevity_hub', 'wellness'),
    long_stress: () => pack('wellness', 'mental_health_support', 'longevity_hub'),
    long_longevity: () => pack('longevity_hub', 'wellness', 'research_tool'),
    long_libido: () => pack('libido_screening', 'wellness', 'longevity_hub'),

    deep_default: () => pack('research_tool', 'integration', 'psychedelic_therapy_hub'),
    deep_evidence: () => pack('research_tool', 'psychedelic_therapy_hub', 'psilocybin'),
    deep_integration: () => pack('integration', 'preparation_assistant', 'community'),
    deep_substances: () => pack('substance_guides', 'psilocybin', 'ketamine'),
    deep_mechanisms: () => pack('psychedelic_therapy_hub', 'mental_health_support', 'research_tool'),
    deep_experts: () => pack('human_journeys_podcast', 'community', 'psychedelic_therapy_hub'),
};

export function getRecommendationPack(navState) {
    const audience = navState.audience || classifyAudience(navState);
    const q2Key = navState.q2Key;

    if (q2Key && PACKS[q2Key]) return PACKS[q2Key]();

    const defaults = {
        [AUDIENCES.CURIOUS]: 'curious_default',
        [AUDIENCES.MENTAL_HEALTH]: 'mh_default',
        [AUDIENCES.LONGEVITY]: 'long_default',
        [AUDIENCES.DEEP_DIVER]: 'deep_default',
    };

    const key = defaults[audience] || 'curious_default';
    return PACKS[key]();
}

export function buildPathSteps(recommendation) {
    return [
        { step: 'primary', label: 'Start here', ...recommendation.primary },
        { step: 'supporting_1', label: 'Then explore', ...recommendation.supporting1 },
        { step: 'supporting_2', label: 'Go deeper', ...recommendation.supporting2 },
    ];
}

export function buildPathMessage() {
    return "Based on your answers, I'd start you with this path:\n\nYour starting path";
}

export function getStepNumber(step) {
    const map = { q1: 1, q2: 2, q3: 3, q4: 4 };
    return map[step] || 0;
}

export function nextStep(currentStep) {
    if (currentStep === 'intro') return 'q1';
    if (currentStep === 'q1') return 'q2';
    if (currentStep === 'q2') return 'q3';
    if (currentStep === 'q3') return 'q4';
    if (currentStep === 'q4') return 'complete';
    return 'complete';
}
